/**
 * Subscription utility functions for filtering and validating subscriptions
 */

/**
 * Filters subscriptions to return only valid ones based on business rules
 *
 * A subscription is considered valid if ALL of these conditions are met:
 * 1. Status is 'active' (not 'cancelled' or 'paused')
 * 2. Iteration limit not exceeded (if applicable)
 * 3. Entry pass tickets not exhausted (if entry-pass type)
 * 4. Event transaction limit not exhausted (if applicable)
 *
 * @param {Array} subscriptions - Array of subscription objects from backend
 * @returns {Array} Filtered array of valid subscriptions
 *
 * @example
 * const validSubscriptions = filterValidSubscriptions(data.seasonTickets);
 * // Returns only subscriptions that pass all validation rules
 */
export const filterValidSubscriptions = (subscriptions) => {
    if (!subscriptions || !Array.isArray(subscriptions)) {
        return [];
    }

    return subscriptions.filter(subscription => {
        // 1. Must have active status
        if (subscription.status !== 'active') {
            return false;
        }

        // 2. If has iteration limit, check it's not exceeded
        // iterations === -1 means unlimited
        if (subscription.iterations !== -1 &&
            subscription.iterationsCompleted >= subscription.iterations) {
            return false;
        }

        // 3. For entry passes, check usage limits
        if (subscription.seasonTicketType === 'entry-pass' &&
            subscription.entryPassTotalTicketsAllowed > 0 &&
            subscription.entryPassTicketsUsed >= subscription.entryPassTotalTicketsAllowed) {
            return false;
        }

        // 4. For season tickets with event transaction limits, check if exhausted
        // eventTransactionAllowed === 0 means no limit (unlimited events)
        if (subscription.eventTransactionAllowed > 0 &&
            subscription.eventTransactionUsed >= subscription.eventTransactionAllowed) {
            return false;
        }

        return true;
    });
};

/**
 * Gets the valid HRC code for discount application
 *
 * Priority order:
 * 1. If user's selectedHrc belongs to a valid subscription → use it
 * 2. Otherwise → use first valid subscription's HRC, sorted by:
 *    a. Limited subscriptions first (eventTransactionAllowed > 0) — consume limited before unlimited
 *    b. Then by start date, newest first
 * 3. If no valid subscriptions → return empty string
 *
 * @param {Array} validSubscriptions - Array of valid subscriptions (already filtered)
 * @param {string} selectedHrc - User's currently selected HRC code
 * @returns {string} HRC code to use, or empty string if none available
 *
 * @example
 * const validSubscriptions = filterValidSubscriptions(data.seasonTickets);
 * const hrc = getValidHrcCode(validSubscriptions, endUser.selectedHrc);
 * localStorage.setItem('hrcCode', hrc);
 */
export const getValidHrcCode = (validSubscriptions, selectedHrc) => {
    if (!validSubscriptions || validSubscriptions.length === 0) {
        return "";
    }

    // Sort: limited subscriptions first (eventTransactionAllowed > 0), then by start date newest first.
    // This ensures limited-use subscriptions are consumed before unlimited ones (-1).
    const sorted = [...validSubscriptions].sort((a, b) => {
        const aLimited = a.eventTransactionAllowed > 0 ? 0 : 1;
        const bLimited = b.eventTransactionAllowed > 0 ? 0 : 1;
        if (aLimited !== bLimited) return aLimited - bLimited;
        return new Date(b.startDate) - new Date(a.startDate);
    });

    // Validate selectedHrc belongs to a valid subscription
    const isSelectedHrcValid = sorted.some(sub => sub.hrc === selectedHrc);

    // Return selectedHrc if valid, otherwise first valid subscription's HRC
    return (isSelectedHrcValid ? selectedHrc : null) || sorted[0]?.hrc || "";
};
